import { consentDocument } from '@toothsomatics/consent-catalog';
import { useRef } from 'react';

import type { ImageDraft } from './types.js';

/**
 * Images a client chose to share, for one position.
 *
 * The framing is the product's position rather than a wording preference. A client **may** share
 * photographs of their own mouth; nothing here asks anyone for imaging held by a practice. This
 * platform is educational and reflective and is not a record system, so it does not request
 * clinical records — and the vocabulary for them is forbidden outright (CLAUDE.md, law 1).
 *
 * Gate 6 (`photo_upload`) guards `POST /v1/submissions/{id}/uploads/presign`. What follows is the
 * *presentation* of that gate: the words come from `packages/consent-catalog` so this screen and
 * the server can never show and enforce different texts. **The tick here decides nothing.** The
 * server resolves the active document version itself, verifies its checksum, writes its own record,
 * and answers 409 CONSENT_REQUIRED to any presign request without one — whatever this component
 * believes.
 *
 * Nothing is uploaded yet: the submissions module does not exist. Files are held in the tab and go
 * no further, and the notice below says so rather than implying a safety that is not there.
 */

/** An allow-list, never a deny-list. Anything not named here is not offered to the picker. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;

/** Generous for a photograph, small enough that a mistake is caught before it is uploaded. */
const MAX_BYTES = 15 * 1024 * 1024;

function readableSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024)).toString()} KB`;
}

export interface UploadSectionProps {
  readonly toothNumber: number;
  readonly images: readonly ImageDraft[];
  /** Scoped to the whole submission, not to one position — the gate's scope is `submission`. */
  readonly consented: boolean;
  readonly onConsentChange: (accepted: boolean) => void;
  readonly onAdd: (files: readonly File[]) => void;
  readonly onRemove: (id: string) => void;
}

export function UploadSection({
  toothNumber,
  images,
  consented,
  onConsentChange,
  onAdd,
  onRemove,
}: UploadSectionProps) {
  const input = useRef<HTMLInputElement>(null);
  const terms = consentDocument('photo_upload');

  const rejected: string[] = [];

  const accept = (list: FileList | null) => {
    if (!list) return;

    const kept = [...list].filter((file) => {
      if (!ACCEPTED.includes(file.type as (typeof ACCEPTED)[number])) {
        rejected.push(`${file.name} is not an image we can accept`);
        return false;
      }
      if (file.size > MAX_BYTES) {
        rejected.push(`${file.name} is larger than ${readableSize(MAX_BYTES)}`);
        return false;
      }
      return true;
    });

    if (kept.length > 0) onAdd(kept);
    // Cleared so choosing the same file twice in a row still fires a change event.
    if (input.current) input.current.value = '';
  };

  return (
    <section className="upload" aria-labelledby={`upload-${String(toothNumber)}`}>
      <h3 className="upload__title" id={`upload-${String(toothNumber)}`}>
        Images you would like to share
      </h3>
      <p className="upload__intro">
        Entirely optional. Only add an image if you want to — your reflection does not depend on it.
      </p>

      {/* Its own tick, its own record. Never bundled with anything else (docs/04). */}
      <label className="upload__consent">
        <input
          type="checkbox"
          checked={consented}
          onChange={(event) => onConsentChange(event.target.checked)}
        />
        <span>{terms.acknowledgement}</span>
      </label>

      <details className="upload__terms">
        <summary>What you are agreeing to</summary>
        {terms.body.split('\n\n').map((paragraph) => (
          <p key={paragraph.slice(0, 24)}>{paragraph}</p>
        ))}
      </details>

      {consented ? (
        <>
          {/*
            The input sits transparently on top of its own label rather than being hidden off to
            one side. A visually-hidden input is still focusable, and clicking the label focused
            something positioned outside the panel — so the scroll container jumped to the bottom
            to bring it into view. Here the thing being focused is exactly where the pointer
            already is, so nothing moves. It is also still a real file input for the keyboard.
          */}
          <label className="button button--quiet upload__choose">
            Choose an image
            <input
              ref={input}
              className="upload__input"
              type="file"
              accept={ACCEPTED.join(',')}
              multiple
              onChange={(event) => accept(event.target.files)}
            />
          </label>
          <p className="upload__limits">
            JPEG, PNG, WebP or HEIC, up to {readableSize(MAX_BYTES)} each.
          </p>
        </>
      ) : (
        <p className="upload__limits">
          The tick above opens this. You can decline and carry on — nothing else changes.
        </p>
      )}

      {rejected.length > 0 ? (
        <ul className="upload__rejected">
          {rejected.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}

      {images.length > 0 ? (
        <ul className="upload__list">
          {images.map((image) => (
            <li key={image.id} className="upload__item">
              <img className="upload__thumb" src={image.previewUrl} alt="" />
              <span className="upload__name">
                {image.name}
                <span className="upload__size">{readableSize(image.size)}</span>
              </span>
              <button
                type="button"
                className="upload__remove"
                onClick={() => onRemove(image.id)}
                aria-label={`Remove ${image.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="upload__pending">
        These are held in this tab only and have not been sent anywhere. Uploading arrives with the
        rest of the submission.
      </p>
    </section>
  );
}
